import { ClippingStudioClip } from "app/js/src/domain/clipping-studio/clipping-studio-clip";
import { AppendedMamSegment } from "app/js/src/domain/clipping-studio/clipping-studio-project";
import { resolveMamFileTimeMs } from "app/js/src/infrastructure/clipping-studio/clipping-studio-mam-file-time";

export interface TranscodedSourceResolve {
  mediaId: number;
  timeSec: number;
}

const DEFAULT_LOAD_TIMEOUT_MS = 30000;
const DEFAULT_SEEK_TIMEOUT_MS = 10000;

/**
 * Resolves source-timeline timeMs to the mezzanine media id and local file time (seconds).
 * Mirrors video-preview resolveTimelineTimeToSource branching without requiring DOM video elements.
 */
export function resolveTranscodedSourceAtTime(args: {
  timeMs: number;
  mediaId: number | null | undefined;
  mainDurationMs: number;
  appendedMamSegments: AppendedMamSegment[];
  isEditMode: boolean;
  editModeClips: ClippingStudioClip[];
}): TranscodedSourceResolve | null {
  const timeMs = Math.max(0, args.timeMs);
  const mainId = args.mediaId != null && args.mediaId > 0 ? args.mediaId : null;
  const mainDurationMs = Math.max(0, args.mainDurationMs);
  const appended = args.appendedMamSegments ?? [];

  if (args.isEditMode) {
    const editMam = (args.editModeClips ?? [])
      .filter((c) => c.mamMediaId != null && c.mamMediaId > 0)
      .slice()
      .sort((a, b) => a.order - b.order);
    const clip = editMam.find((c) => timeMs >= c.inMs && timeMs < c.outMs);
    if (clip?.mamMediaId != null) {
      const timeInClipMs = resolveMamFileTimeMs(
        timeMs,
        clip,
        appended,
        mainDurationMs,
      );
      return {
        mediaId: clip.mamMediaId,
        timeSec: Math.max(0, timeInClipMs / 1000),
      };
    }
  }

  if (appended.length === 0 || timeMs < mainDurationMs) {
    if (mainId == null) return null;
    return { mediaId: mainId, timeSec: Math.max(0, timeMs / 1000) };
  }

  let cursorMs = mainDurationMs;
  for (const seg of appended) {
    const endMs = cursorMs + Math.max(0, seg.durationMs ?? 0);
    if (timeMs < endMs) {
      if (seg.mamMediaId == null || seg.mamMediaId <= 0) return null;
      const timeInSegmentMs = timeMs - cursorMs;
      return {
        mediaId: seg.mamMediaId,
        timeSec: Math.max(0, timeInSegmentMs / 1000),
      };
    }
    cursorMs = endMs;
  }

  if (mainId == null) return null;
  return { mediaId: mainId, timeSec: Math.max(0, mainDurationMs / 1000) };
}

function waitForEvent(
  target: EventTarget,
  eventName: string,
  timeoutMs: number,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      cleanup();
      reject(new Error(`${eventName} timeout`));
    }, timeoutMs);
    const onEvent = (): void => {
      cleanup();
      resolve();
    };
    const cleanup = (): void => {
      clearTimeout(timer);
      target.removeEventListener(eventName, onEvent);
    };
    target.addEventListener(eventName, onEvent, { once: true });
  });
}

export function disposeEphemeralVideo(video: HTMLVideoElement | null | undefined): void {
  if (!video) return;
  try {
    video.pause();
    video.removeAttribute("src");
    video.load();
  } catch {
    // ignore dispose errors
  }
}

/**
 * Loads an ephemeral video at url, seeks to timeSec, runs fn, then always disposes the element.
 */
export async function withEphemeralTranscodedVideo<T>(
  url: string,
  timeSec: number,
  fn: (video: HTMLVideoElement) => Promise<T>,
  options?: { loadTimeoutMs?: number; seekTimeoutMs?: number },
): Promise<T> {
  const trimmed = url?.trim();
  if (!trimmed) {
    throw new Error("Transcoded URL is empty.");
  }

  const loadTimeoutMs = options?.loadTimeoutMs ?? DEFAULT_LOAD_TIMEOUT_MS;
  const seekTimeoutMs = options?.seekTimeoutMs ?? DEFAULT_SEEK_TIMEOUT_MS;
  const video = document.createElement("video");
  video.crossOrigin = "anonymous";
  video.preload = "auto";
  video.muted = true;
  video.playsInline = true;

  try {
    video.src = trimmed;
    video.load();
    if (video.readyState < 2) {
      await waitForEvent(video, "loadeddata", loadTimeoutMs);
    }
    if (!video.videoWidth) {
      throw new Error("Transcoded video has no dimensions.");
    }

    const seekTo = Math.max(0, timeSec);
    if (Math.abs(video.currentTime - seekTo) > 0.01) {
      const seeked = waitForEvent(video, "seeked", seekTimeoutMs);
      video.currentTime = seekTo;
      await seeked;
    }

    // Allow one frame paint after seek (same idea as content-control screenshot).
    await new Promise<void>((resolve) => requestAnimationFrame(() => resolve()));

    return await fn(video);
  } finally {
    disposeEphemeralVideo(video);
  }
}

/** Draws the current video frame to a JPEG data URL at native resolution. */
export function drawVideoFrameToJpegDataUrl(
  video: HTMLVideoElement,
  jpegQuality = 0.9,
): string | null {
  if (video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
    return null;
  }
  try {
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.drawImage(video, 0, 0);
    return canvas.toDataURL("image/jpeg", jpegQuality);
  } catch {
    return null;
  }
}

/**
 * Fetches TranscodedFile URL, loads ephemeral video at timeSec, returns JPEG data URL.
 * Always disposes the video element.
 */
export async function captureTranscodedFrameDataUrl(args: {
  getTranscodedUrl: (mediaId: number) => Promise<string>;
  mediaId: number;
  timeSec: number;
  jpegQuality?: number;
}): Promise<string | null> {
  const url = await args.getTranscodedUrl(args.mediaId);
  return withEphemeralTranscodedVideo(url, args.timeSec, async (video) =>
    drawVideoFrameToJpegDataUrl(video, args.jpegQuality ?? 0.9),
  );
}
